// src/components/AuthModal.js
// Login / signup dialog. Matches the backend UserRegister schema
// (email, username, password, language, date_of_birth, accepted_eula).

import { useState } from "react";
import { auth, setToken } from "../api";

export default function AuthModal({ onClose, onAuthed, onOpenLegal, initialTab = "login" }) {
  const [mode, setMode] = useState(initialTab === "signup" ? "register" : "login");
  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
    date_of_birth: "",
    accepted_eula: false,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // password-reset sub-flow: "email" → "otp" → "password"
  const [reset, setReset] = useState(null);
  const [resetEmail, setResetEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [note, setNote] = useState("");

  const runReset = async (e) => {
    e.preventDefault();
    setErr("");
    setNote("");
    setBusy(true);
    try {
      if (reset === "email") {
        await auth.forgotPassword(resetEmail);
        setNote("We emailed you a 6-digit code.");
        setReset("otp");
      } else if (reset === "otp") {
        await auth.verifyOtp(resetEmail, otp);
        setReset("password");
      } else {
        await auth.resetPassword(resetEmail, newPw);
        setNote("Password updated — you can log in now.");
        setReset(null);
        setMode("login");
      }
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      let data;
      if (mode === "login") {
        data = await auth.login(form.email, form.password);
      } else {
        if (!form.accepted_eula) throw new Error("Please accept the EULA to continue.");
        data = await auth.register({
          email: form.email,
          username: form.username,
          password: form.password,
          language: "en",
          date_of_birth: form.date_of_birth,
          accepted_eula: form.accepted_eula,
        });
      }
      setToken(data.token);
      onAuthed(data.user);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  // ----- password reset sub-flow -----
  if (reset) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal__tabs">
            <button className="active">Reset password</button>
          </div>
          <form className="modal__body" onSubmit={runReset}>
            {reset === "email" && (
              <label>
                Account email
                <input value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} type="email" required autoFocus />
              </label>
            )}
            {reset === "otp" && (
              <label>
                6-digit code
                <input value={otp} onChange={(e) => setOtp(e.target.value)} type="text" inputMode="numeric" maxLength={6} required autoFocus />
              </label>
            )}
            {reset === "password" && (
              <label>
                New password
                <input value={newPw} onChange={(e) => setNewPw(e.target.value)} type="password" minLength={6} required autoFocus />
              </label>
            )}
            {note && <div className="form-note">{note}</div>}
            {err && <div className="form-error">{err}</div>}
            <button className="btn btn--primary btn--block" disabled={busy}>
              {busy ? "Please wait…" : reset === "email" ? "Send code" : reset === "otp" ? "Verify code" : "Set password"}
            </button>
            <button type="button" className="link-btn" onClick={() => { setReset(null); setErr(""); setNote(""); }}>
              ← Back to log in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__tabs">
          <button className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>Log in</button>
          <button className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>Sign up</button>
        </div>

        <form className="modal__body" onSubmit={submit}>
          <label>
            Email {mode === "login" ? "or username" : ""}
            <input value={form.email} onChange={set("email")} type="text" required autoFocus />
          </label>

          {mode === "register" && (
            <>
              <label>
                Username
                <input value={form.username} onChange={set("username")} type="text" required />
              </label>
              <label>
                Date of birth
                <input value={form.date_of_birth} onChange={set("date_of_birth")} type="date" required />
              </label>
            </>
          )}

          <label>
            Password
            <input value={form.password} onChange={set("password")} type="password" required minLength={6} />
          </label>

          {mode === "register" && (
            <label className="checkbox">
              <input type="checkbox" checked={form.accepted_eula} onChange={set("accepted_eula")} />
              <span>
                I am 18+ and accept the{" "}
                <button type="button" className="inline-link" onClick={() => onOpenLegal?.("eula")}>EULA</button>{" "}&amp;{" "}
                <button type="button" className="inline-link" onClick={() => onOpenLegal?.("content-policy")}>Content Policy</button>.
              </span>
            </label>
          )}

          {err && <div className="form-error">{err}</div>}

          <button className="btn btn--primary btn--block" disabled={busy}>
            {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
          </button>

          {mode === "login" && (
            <button type="button" className="link-btn" onClick={() => { setReset("email"); setResetEmail(form.email); setErr(""); }}>
              Forgot password?
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
